import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from "@angular/core"
import { MatDialog } from "@angular/material"
import { MapMarker } from "src/app/models/marker"
import { MapService } from "src/app/services/map.service"
import { ITALY_CENTER } from "src/configs/map_configuration"
import { environment } from "src/environments/environment"

@Component({
	selector: "app-gmap",
	templateUrl: "./gmap.component.html",
	styleUrls: ["./gmap.component.scss"]
})
export class GMapComponent implements OnInit {
  @ViewChild('gmap', { static: true }) gmapElement: any;
  @Output() mapReady = new EventEmitter<google.maps.Map>();
  @Output() markerClicked = new EventEmitter<any>();
  @Output() update = new EventEmitter<any>();
  @Output() clickedButton = new EventEmitter<any>();
  map: google.maps.Map;
  public picked: MapMarker;
  public markersShown = [];
  public notEmitBounds = false;
  public assets_version = environment.ASSETS_VERSION;
  private intervalUpdateEvent: any;

  constructor(public mapService: MapService, public dialog: MatDialog, private cdr: ChangeDetectorRef) { }

	ngOnInit() {
		this.mapService.mapReadyEvent.subscribe(ready => {
			if (ready) {
				const mapProp = {
					center: ITALY_CENTER,
					zoom: 6,
					mapTypeId: google.maps.MapTypeId.ROADMAP,
					mapTypeControl: false,
					streetViewControl: false,
					disableDoubleClickZoom: false,
					scrollwheel: true,
					zoomControlOptions: { position: google.maps.ControlPosition.TOP_LEFT },
					fullscreenControlOptions: { position: google.maps.ControlPosition.LEFT_TOP }
				}
				this.map = new google.maps.Map(this.gmapElement.nativeElement, mapProp)
				this.map.setOptions({
					styles: [
						{
							featureType: "poi.business",
							stylers: [{ visibility: "off" }]
						},
						{
							featureType: "poi.government",
							stylers: [{ visibility: "off" }]
						},
						{
							featureType: "poi.medical",
							stylers: [{ visibility: "off" }]
						},
						{
							featureType: "poi.place_of_worship",
							stylers: [{ visibility: "off" }]
						},
						{
							featureType: "poi.school",
							stylers: [{ visibility: "off" }]
						},
						{
							featureType: "poi.sports_complex",
							stylers: [{ visibility: "off" }]
						},
						{
							featureType: "transit",
							elementType: "labels.icon",
							stylers: [{ visibility: "off" }]
						}
					]
				})

        // Listeners
        google.maps.event.addListener(this.map, 'idle', () => {
          this.emitUpdate();
        });
        this.mapReady.emit(this.map);
      }
    });
    if (!environment.LOCAL) {
      this.mapService.sendInitConfirm();
    }
  }

  private emitUpdate() {
		clearTimeout(this.intervalUpdateEvent)
		this.intervalUpdateEvent = setTimeout(() => {
			this.update.emit(null)
		}, 500)
	}

  public onClickMapButton(type) {
    this.clickedButton.emit(type);
  }

  // Markers
  private drawMarkers(markers) {
    if (!markers) {
      return;
    }
    markers.forEach((m) => {
      if (!m) {
        return;
      }
      const infowindow = new google.maps.InfoWindow({});
      let iconMarker: any;
      if (m.icons) {
        const zoomMap = this.map.getZoom();
        const found = m.icons.findIndex(i => zoomMap >= i.zoom_from && zoomMap <= i.zoom_to && (i.area_type ? i.area_type === m.area_type : true));
        if (found >= 0) {
          iconMarker = m.icons[found].path;
        }
      }
      const marker = new google.maps.Marker({
        position: m.coordinates,
        icon: iconMarker,
        label: m.label_inside ? { text: m.label, color: 'white' } : null,
        clickable: m.can_click
      });
      marker['type'] = m.type;
      marker['range'] = m.range;
      if (m.can_hover) {
        marker.addListener('mouseover', () => {
          infowindow.setContent(m.label);
          infowindow.open(this.map, marker);
        });
        marker.addListener('mouseout', () => {
          infowindow.close();
        });
      }
      if (m.can_click) {
        marker.addListener('click', () => {
          this.markerClicked.emit(m);
        });
      }
      // marker.setMap(this.map);
      const zoom = this.map.getZoom();
      if ((m.range.min !== null && zoom >= m.range.min) && (m.range.max == null || zoom < m.range.max)) {
        marker.setMap(this.map);
      } else if ((m.range.min == null || zoom < m.range.min) || zoom > m.range.max) {
        marker.setMap(null);
      }
      this.markersShown.push(marker);
    });
    this.cdr.detectChanges();
  }

	public showMarkers(markers, opts?) {
		if (opts && opts.clearTypes) {
			this.removeMarkersByTypes(opts.clearTypes)
		}
		this.drawMarkers(markers)
	}

	public removeMarkersByTypes(types: String[]) {
		for (const type of types) {
			this.removeMarkersByType(type)
		}
	}

	public removeMarkersByType(type: String) {
		const list = this.markersShown.filter(e => e.type === type)
		for (const m of list) {
			m.setMap(null)
		}
		this.markersShown = this.markersShown.filter(e => e.type !== type)
	}

	public removeMarkersByAreaType(areaType) {
		const list = this.markersShown.filter(e => e.area_type === areaType)
		for (const m of list) {
			m.setMap(null)
		}
		this.markersShown = this.markersShown.filter(e => e.area_type !== areaType)
	}

	public removeMarkerByIcon(iconName) {
		const list = this.markersShown.filter(e => (e.icon && e.icon.url && e.icon.url.includes(iconName)) || !e.icon)
		if (list) {
			for (const m of list) {
				m.setMap(null)
			}
		}
		this.markersShown = this.markersShown.filter(e => e.icon && e.icon.url && !e.icon.url.includes(iconName))
	}

	public zoomToCoordinates(latlng, zoom = null, notEmitBounds = true) {
		zoom = zoom ? zoom : 21
		this.map.setZoom(zoom)
		this.map.setCenter(latlng)
		this.notEmitBounds = notEmitBounds
		setTimeout(() => (this.notEmitBounds = false), 500)
	}

	public centerMapToCoordinates(latlng, notEmitBounds = true) {
		this.map.setCenter(latlng)
		this.notEmitBounds = notEmitBounds
		setTimeout(() => (this.notEmitBounds = false), 500)
	}

	public fitMapFromMarkers(markers) {
		const bounds = new google.maps.LatLngBounds()
		for (const marker of markers) {
			const latlng = new google.maps.LatLng(marker.coordinates.lat, marker.coordinates.lng)
			bounds.extend(latlng)
		}
		this.map.fitBounds(bounds)
	}
}
